import React, { useState, useCallback, CSSProperties, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import notifyStyles from "../notification/NotifySurface.module.css";
import { Formik, Form, FastField, Field, useFormikContext, FormikHelpers } from "formik";
import * as Yup from "yup";
import { Tooltip } from "@mui/material";
import { useGlobalState } from "../../context/useGlobalState";
import { createActivity } from "../../api/activity";
import Button from "../../components/ui/Button";
import { FileInput } from "../../components/common/FileInput";
import { uploadFileToS3Complete } from "../../api/s3";
import ImageList from "../../components/images/ImageList";
import { TextField, DropdownField, NumberField, RadioField } from "../../components/common/FormFields";
import FormGroup from "../../components/common/FormGroup";
import ImageSpecsBadges from "../../components/common/ImageSpecsBadges";
import { useImageValidation } from "../../hooks/useImageValidation";
import { IMAGE_FORMAT, IMAGE_MAX_SIZE_200_KB } from "../../utils/image";
import ActivityStatusModal from "./ActivityCreationModal";
import ActivityTypeField from "./ActivityTypeField";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import activeRadio from "../../assets/images/radio_active.png";
import inactiveRadio from "../../assets/images/radio_inactive.png";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { isVdpCmsHost } from "../../utils/cmsHost";

// Types
interface ActivityFormValues {
    isOnboard: boolean;
    coverImage: string;
    duration: number;
    position: number;
    price: number;
    promotionPercentage: number;
    coordinates: string; // e.g. "48.8566, 2.3522"
    name: string;
    url: string;
    contentLanguage: string;
    cityId: string;
    activityPCVTypeId: string;
}

// Form content
const ActivityForm = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { data } = useGlobalState();
    const { values, setFieldValue } = useFormikContext<ActivityFormValues>();
    const { coverImage } = values;
    const showOnboardField = useMemo(() => isVdpCmsHost(), []);
    const imageValidation = useImageValidation({
        type: "activity",
        imageUrl: coverImage || undefined,
    });

    useEffect(() => {
        if (!showOnboardField) {
            setFieldValue("isOnboard", false);
        }
    }, [showOnboardField, setFieldValue]);

    // Location handling, aligned with notifications: city is mandatory but when the
    // company has a single city we auto-fill it and hide the picker.
    const cityOptions = useMemo(() => data?.locationCityOptions || [], [data?.locationCityOptions]);
    const hasSingleCity = cityOptions.length === 1;

    useEffect(() => {
        if (hasSingleCity && !values.cityId) {
            setFieldValue("cityId", cityOptions[0].value);
        }
    }, [hasSingleCity, cityOptions, values.cityId, setFieldValue]);

    const handleFileSelected = useCallback(
        async (files: File | File[]) => {
            const file = Array.isArray(files) ? files[0] : files;
            if (file) {
                const token = localStorage.getItem("token");
                if (!token) {
                    console.error("No authentication token found");
                    return;
                }

                await imageValidation.validateImage(file);

                uploadFileToS3Complete(
                    {
                        file,
                        isPublic: true, // Public files for general uploads
                        isFolder: false, // No folder structure for general uploads
                    },
                    token,
                )
                    .then((result) => {
                        setFieldValue("coverImage", result);
                    })
                    .catch(console.error);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setFieldValue, imageValidation.validateImage],
    );

    const setPicturesFromList = useCallback(
        (pictures: string[]) => {
            setFieldValue("coverImage", pictures[0] || null);
        },
        [setFieldValue],
    );

    console.log(data);

    return (
        <Form>
            <h1 className={notifyStyles.sidebarSettingsTitle}>{t("contentSettings")}</h1>

            <div style={{ marginTop: "0" }}>
                {!hasSingleCity && (
                    <FormGroup title={t("location")} subtitle={t("activityLocationSub")} required>
                        <Field
                            name="cityId"
                            options={cityOptions}
                            placeholder={t("selectALocation")}
                            component={DropdownField}
                        />
                    </FormGroup>
                )}

                <ActivityTypeField title={t("activityType")} subtitle={t("activityTypeSub")} required />

                <FormGroup
                    title={t("chooseName")}
                    subtitle={t("chooseNameSub")}
                    tooltip={t("doNotTranslateTooltip")}
                    required
                >
                    <FastField
                        name="name"
                        label={t("name")}
                        placeholder={t("name")}
                        errorMessage={t("invalidName")}
                        component={TextField}
                    />
                </FormGroup>

                <FormGroup title={t("chooseUrl")} subtitle={t("chooseUrlSub")} required>
                    <FastField
                        name="url"
                        label={t("url")}
                        placeholder={t("url")}
                        errorMessage={t("invalidUrl")}
                        component={TextField}
                    />
                </FormGroup>

                <FormGroup title={t("Language")} subtitle={t("selectLanguageActivity")} required>
                    <Field
                        name="contentLanguage"
                        options={data.languagesAI}
                        placeholder={t("selectALanguage")}
                        component={DropdownField}
                    />
                </FormGroup>

                <FormGroup title={t("coordinates")} subtitle={t("enterCoordinatesLatLng")}>
                    <FastField
                        name="coordinates"
                        label={t("Coordinates")}
                        placeholder="48.8566, 2.3522"
                        errorMessage={t("Invalid coordinates")}
                        component={TextField}
                    />
                </FormGroup>

                <FormGroup title={t("uploadYourActivityImage")} subtitle={t("uploadYourActivityImageSub")} required>
                    <ImageSpecsBadges spec="activity" warnings={imageValidation.warnings} />
                    <div className={notifyStyles.fileUpload} style={{ maxWidth: 455 }}>
                        <FileInput
                            onFileSelected={handleFileSelected}
                            showButton={true}
                            buttonVariant="outline"
                            buttonText={t("add")}
                            acceptedFileTypes={IMAGE_FORMAT}
                            maxSize={IMAGE_MAX_SIZE_200_KB}
                            maxSizeLabel={t("imageMaxSize200kb")}
                        />
                    </div>
                    {coverImage && (
                        <div className="photo-gallery" style={{ maxWidth: 455 }}>
                            <ImageList pictures={coverImage ? [coverImage] : []} setPictures={setPicturesFromList} />
                        </div>
                    )}
                </FormGroup>

                {showOnboardField && (
                    <FormGroup
                        title={t("isOnBoard")}
                        className={notifyStyles.stepDescription}
                        style={{ marginTop: 20 }}
                    >
                        <Field
                            name="isOnboard"
                            component={RadioField}
                            value={true}
                            label={t("isOnBoardTrue")}
                            subtitle={t("isOnBoardTrueSubtitle")}
                            activeImage={activeRadio}
                            inactiveImage={inactiveRadio}
                        />
                        <Field
                            name="isOnboard"
                            component={RadioField}
                            value={false}
                            label={t("isOnBoardFalse")}
                            subtitle={t("isOnBoardFalseSubtitle")}
                            activeImage={activeRadio}
                            inactiveImage={inactiveRadio}
                        />
                    </FormGroup>
                )}

                <FormGroup title={t("choosePrice")} subtitle={t("choosePriceSub")}>
                    <FastField
                        name="price"
                        label={t("price")}
                        placeholder={t("price")}
                        errorMessage={t("invalidPrice")}
                        component={TextField}
                    />
                </FormGroup>

                <FormGroup title={t("activityDuration")} subtitle={t("activityDurationSub")}>
                    <div className={notifyStyles.numRow}>
                        <FastField
                            name="duration"
                            label={t("duration")}
                            placeholder={t("durationPlaceholder")}
                            errorMessage={t("invalidDuration")}
                            component={NumberField}
                            min={0}
                        />
                        <span className={notifyStyles.numUnit}>min</span>
                    </div>
                </FormGroup>

                <FormGroup
                    title={t("activityPosition")}
                    subtitle={t("activityPositionSub")}
                    tooltip={t("activityPositionInfo")}
                >
                    <div className={notifyStyles.numRow}>
                        <FastField
                            name="position"
                            label={t("position")}
                            placeholder={t("positionPlaceholder")}
                            errorMessage={t("invalidPosition")}
                            component={NumberField}
                            min={0}
                        />
                    </div>
                </FormGroup>

                {/* <FormGroup title={t("choosePromotionPercentage")} subtitle={t("choosePromotionPercentage")}>
                    <FastField
                        name="promotionPercentage"
                        label={t("promotionPercentage")}
                        placeholder={t("promotionPercentage")}
                        errorMessage={t("invalidPromotionPercentage")}
                        component={NumberField}
                        min={0}
                        max={100}
                    />{" "}
                    <span style={{ fontSize: "14px" }}>%</span>
                </FormGroup> */}
            </div>
        </Form>
    );
};

const ActivityPreview = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const RecenterMap = ({ lat, lng }: { lat: number; lng: number }) => {
        const map = useMap();

        useEffect(() => {
            if (!isNaN(lat) && !isNaN(lng)) {
                map.setView([lat, lng]);
            }
        }, [lat, lng, map]);

        return null;
    };
    const { values } = useFormikContext<ActivityFormValues>();
    const { coordinates } = values;

    const [lat, lng] = coordinates?.split(",").map((val) => parseFloat(val.trim()));

    if (isNaN(lat) || isNaN(lng)) {
        return <p style={{ padding: "1rem" }}>{t("entreValidCoordinatesToSeeMap")}</p>;
    }

    const yellowCircleIcon = L.divIcon({
        className: "custom-yellow-icon",
        // Inline styles: Leaflet injects this HTML outside CSS-module class trees,
        // so module-scoped rules alone are unreliable after the global.css migration.
        html: `<div class="custom-yellow-marker" style="width:20px;height:20px;border-radius:50%;background:#f5c518;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.35);box-sizing:border-box;"></div>`,
        iconSize: [20, 20],
        iconAnchor: [10, 10],
    });

    return (
        <div className={notifyStyles.notificationPreviewContainer} style={{ height: "100%", minHeight: "420px" }}>
            <MapContainer
                center={[lat, lng]}
                zoom={10}
                minZoom={5}
                style={{ height: "100%", minHeight: "420px", width: "100%", borderRadius: 12 }}
            >
                <TileLayer
                    attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker position={[lat, lng]} icon={yellowCircleIcon} />
                <RecenterMap lat={lat} lng={lng} />
            </MapContainer>
        </div>
    );
};

interface NewActivitiesProps {
    setActiveTab: (tab: "newActivity" | "activeActivities") => void;
}

// Page Container
const NewActivities: React.FC<NewActivitiesProps> = ({ setActiveTab }) => {
    const { t: rawT, i18n } = useTranslation();
    const t = rawT as any;
    const [status, setStatus] = useState<"waiting" | "success" | "error">("waiting");
    const [modalOpen, setModalOpen] = useState(false);

    const fixedButtonContainerStyle: CSSProperties = {
        position: "fixed",
        bottom: "0",
        left: "0",
        right: "0",
        display: "flex",
        justifyContent: "center",
        zIndex: 1000,
        padding: "20px",
        backgroundColor: "rgba(255, 255, 255)",
        boxShadow: "0 -2px 10px rgba(0, 0, 0, 0.1)",
        borderTop: "1px solid #eee",
    };

    const validationSchema = Yup.object({
        isOnboard: Yup.boolean().required(t("requiredIsOnboard")),
        coverImage: Yup.string().required(t("requiredCoverImage")),
        duration: Yup.number().min(0),
        position: Yup.number().min(0),
        price: Yup.number().min(0),
        promotionPercentage: Yup.number().min(0).max(100),
        coordinates: Yup.string()
            .matches(/^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/, t("invalidCoordinatesFormat"))
            .test("is-valid-coordinates", t("invalidCoordinates"), (value) => {
                if (!value) return true; // Optional field
                const [lat, lng] = value.split(",").map((v) => parseFloat(v.trim()));
                return !isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
            }),
        name: Yup.string().required(t("requiredName")),
        url: Yup.string().required(t("requiredUrl")).url(t("invalidUrl")),
        contentLanguage: Yup.string().required(t("requiredContentLanguage")),
        cityId: Yup.string().required(t("requiredCityId")),
        activityPCVTypeId: Yup.string().required(t("requiredActivityType")),
    });

    const initialValues: ActivityFormValues = {
        isOnboard: false,
        coverImage: "",
        duration: 0,
        position: 0,
        price: 0,
        promotionPercentage: 0,
        coordinates: "",
        name: "",
        url: "",
        contentLanguage: "",
        cityId: "",
        activityPCVTypeId: "",
    };

    const handleSubmit = async (values: ActivityFormValues, { resetForm }: FormikHelpers<ActivityFormValues>) => {
        const token = localStorage.getItem("token");
        if (!token) {
            console.error("Missing token");
            return;
        }

        try {
            let lat = 0;
            let lng = 0;
            if (values.coordinates && values.coordinates.trim()) {
                [lat, lng] = values.coordinates.split(",").map((val) => parseFloat(val.trim()));
            }

            const payload = {
                isOnboard: isVdpCmsHost() ? values.isOnboard : false,
                coverImage: values.coverImage || "",
                duration: Number(values.duration) || 0,
                position: Number(values.position) || 0,
                price: Number(values.price) || 0,
                promotionPercentage: Number(values.promotionPercentage) || 0,
                latitude: lat,
                longitude: lng,
                cityId: values.cityId,
                activityPCVTypeId: values.activityPCVTypeId || undefined,
                localized: [
                    {
                        language: values.contentLanguage || "EN",
                        name: values.name,
                        url: values.url,
                    },
                ],
            };

            setModalOpen(true);
            try {
                setStatus("waiting");
                const response = await createActivity({ token, payload });
                console.log("Created activity:", response);
                setStatus("success");
            } catch {
                setStatus("error");
            } finally {
                setTimeout(() => resetForm(), 100);
            }
        } catch (err: any) {
            console.error("Error creating activity:", err);
        }
    };

    return (
        <div className={notifyStyles.notificationPageContainer} style={{ paddingBottom: "80px" }}>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                key={i18n.language}
                validateOnMount
            >
                {({ isSubmitting, submitForm, errors }) => (
                    <>
                        <div className={notifyStyles.notificationLayout}>
                            <div className={`${notifyStyles.notificationSidebar} ${notifyStyles.activitySidebar}`}>
                                <ActivityForm />
                            </div>
                            <div className={notifyStyles.notificationPreviewPanel}>
                                <ActivityPreview />
                            </div>
                        </div>
                        <div style={fixedButtonContainerStyle}>
                            <Tooltip
                                title={
                                    Object.keys(errors).length > 0
                                        ? t("formIncompleteTooltip", {
                                              fields: Object.values(errors).join(", "),
                                          })
                                        : ""
                                }
                                arrow
                            >
                                <span>
                                    <Button
                                        className={notifyStyles.generateButton}
                                        label={t("createActivity")}
                                        onClick={submitForm}
                                        disabled={isSubmitting || Object.keys(errors).length > 0}
                                    />
                                </span>
                            </Tooltip>
                        </div>
                    </>
                )}
            </Formik>
            <ActivityStatusModal
                isOpen={modalOpen}
                status={status}
                onClose={() => setModalOpen(false)}
                onCreateAnother={() => setModalOpen(false)}
                onGoToActive={() => {
                    setModalOpen(false);
                    setActiveTab("activeActivities");
                }}
            />
        </div>
    );
};

export default NewActivities;
